import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';

// ── TypeScript Interfaces ─────────────────────────────────────

type MeetingType = 'Committee' | 'GeneralBody';
type MeetingStatus = 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled';

interface Meeting {
  id: string;
  title: string;
  meetingDate: string;
  meetingType: MeetingType;
  status: MeetingStatus;
  subcommitteeName: string | null;
  subcommitteeId: string | null;
  location: string;
  description: string | null;
}

interface MeetingsResponse {
  data: Meeting[];
  count: number;
}

interface SubcommitteeOption {
  id: string;
  name: string;
}

// ── Constants ─────────────────────────────────────────────────

const STATUS_COLORS: Record<MeetingStatus, string> = {
  Scheduled: '#1976d2',
  InProgress: '#f57c00',
  Completed: '#1a6b3c',
  Cancelled: '#d32f2f',
};

const MEETING_TYPES: { value: MeetingType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'Committee', label: 'Committee' },
  { value: 'GeneralBody', label: 'General Body' },
];

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: 0 },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  filterBar: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  trClickable: { cursor: 'pointer', transition: 'background-color 0.15s' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
};

// ── Component ─────────────────────────────────────────────────

export function MeetingMinutesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data state
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [subcommittees, setSubcommittees] = useState<SubcommitteeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [subcommitteeFilter, setSubcommitteeFilter] = useState('');
  const [meetingTypeFilter, setMeetingTypeFilter] = useState<MeetingType | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const canCreateMeeting = user ? isAtLeast(user.role, 'Organizer') : false;

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    fetchSubcommittees();
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [subcommitteeFilter, meetingTypeFilter, dateFrom, dateTo]);

  async function fetchSubcommittees() {
    try {
      const data = await apiClient<SubcommitteeOption[]>('/subcommittees');
      setSubcommittees(data);
    } catch {
      // Non-critical; dropdown will be empty
    }
  }

  async function fetchMeetings() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (subcommitteeFilter) params.append('subcommitteeId', subcommitteeFilter);
      if (meetingTypeFilter) params.append('meetingType', meetingTypeFilter);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const queryString = params.toString();
      const url = `/meetings${queryString ? `?${queryString}` : ''}`;
      const response = await apiClient<MeetingsResponse>(url);
      setMeetings(response.data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load meetings.');
    } finally {
      setLoading(false);
    }
  }

  // ── Helpers ───────────────────────────────────────────────

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function formatMeetingType(type: MeetingType): string {
    return type === 'GeneralBody' ? 'General Body' : 'Committee';
  }

  // ── Render ────────────────────────────────────────────────

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Meeting Minutes</h1>
        {canCreateMeeting && (
          <button
            style={styles.btn}
            onClick={() => navigate('/meetings/new')}
          >
            + New Meeting
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.section}>
        {/* Filter Bar */}
        <div style={styles.filterBar}>
          <select
            style={styles.select}
            value={subcommitteeFilter}
            onChange={(e) => setSubcommitteeFilter(e.target.value)}
            aria-label="Filter by subcommittee"
          >
            <option value="">All Subcommittees</option>
            {subcommittees.map((sc) => (
              <option key={sc.id} value={sc.id}>
                {sc.name}
              </option>
            ))}
          </select>

          <select
            style={styles.select}
            value={meetingTypeFilter}
            onChange={(e) => setMeetingTypeFilter(e.target.value as MeetingType | '')}
            aria-label="Filter by meeting type"
          >
            {MEETING_TYPES.map((mt) => (
              <option key={mt.value} value={mt.value}>
                {mt.label}
              </option>
            ))}
          </select>

          <input
            type="date"
            style={styles.input}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="Date from"
            placeholder="From"
          />

          <input
            type="date"
            style={styles.input}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="Date to"
            placeholder="To"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div style={styles.loading}>Loading meetings...</div>
        ) : meetings.length === 0 ? (
          <div style={styles.emptyState}>No meetings found.</div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Subcommittee</th>
                </tr>
              </thead>
              <tbody>
                {meetings.map((meeting) => (
                  <tr
                    key={meeting.id}
                    style={styles.trClickable}
                    onClick={() => navigate(`/meetings/${meeting.id}`)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '#f0f7f2';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                    }}
                  >
                    <td style={styles.td}>{meeting.title}</td>
                    <td style={styles.td}>{formatDate(meeting.meetingDate)}</td>
                    <td style={styles.td}>{formatMeetingType(meeting.meetingType)}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          backgroundColor: STATUS_COLORS[meeting.status],
                        }}
                      >
                        {meeting.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {meeting.subcommitteeName || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
